import * as React from 'react';
import { KeyboardEvent, MouseEvent, PointerEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { toast } from 'sonner';
import { DRAW_CONFIG } from './drawConfig.constant';
import { GraphConnection } from './graphComponents/GraphConnection';
import { GraphBackgroundPattern } from './BaseComponents/GraphBackgroundPattern';
import { interpolateVectors, Vector } from '../helpers/Vector';
import type {
    DataConnection,
    FunctionDefinition,
    FunctionNodeInstance,
    FunctionPortDefinition,
    IntegrationAccess,
    NodeInstance,
    NodeInstanceDataPort,
    NodeInstanceState,
    ValidationResult,
} from '@glueapp/graph-types';
import { getStringRepresentationFromType, inherentOutPortTemplate, Node } from '@glueapp/graph-types';
import { ShowNodeLibraryButton } from './navigationLayer/ShowNodeLibraryButton';
import { getInElementPositionFromEvent } from './helpers/getElementInPositionFromEvent';
import { screenToWorldCoordinate } from './helpers/coordinateSystemMapping';
import { CollaboratorCursors } from '../collab/CollaboratorCursors';
import { CollaboratorPresence } from '../collab/useAwareness';
import { useNodeDrag } from './useNodeDrag';
import { useConnectionDrag } from './useConnectionDrag';
import { useViewDrag } from './useViewDrag';
import { useNodeResizing } from './useNodeResizing';
import { useGraphFraming } from './useGraphFraming';
import { useNodeLibrary } from './useNodeLibrary';
import { useZooming } from './useZooming';
import { CentralGraphOverlayMessage, NavigationLayer } from './navigationLayer/NavigationLayer';
import { dictionaryFromNumberTuple, dictionaryFromStringTuple } from '../helpers/dictionaryFromTuple';
import { ExecutionError, ExecutionResult, NodeRunRecordDictionary } from '../useGraphExecution';
import { useCopyAndPaste } from './useCopyAndPaste';
import { useRectangleSelection } from './useRectangleSelection';
import { GraphMessage } from './BaseComponents/GraphMessage';
import { getColorFromDataType } from '../helpers/getColorFromDataType';
import { NodeRenderer } from '@/GraphEditor/drawingLayer/NodeRenderer';
import { ThemeType } from '@/theme/glueTheme';
import { LibraryElement } from '@/GraphEditor/drawingLayer/NodeLibrary/LibraryElement';
import { PortOverlayLayer } from '@/GraphEditor/drawingLayer/portOverlay/PortOverlayLayer';
import type { PortOverlaySide, PortOverlayState } from '@/GraphEditor/drawingLayer/portOverlay/portOverlayTypes';
import type { GraphRunDetail } from '@/GraphEditor/useGraphRuns';
import type { RunCompatibility } from '@/GraphEditor/helpers/runCompatibility';
import { RunInfoBar } from '@/GraphEditor/RunInfoBar';
import type { AgentInstance } from '@/GraphEditor/agent/agentTypes';
import type { AgentManager } from '@/GraphEditor/agent/useAgentManager';
import { AgentInGraphPanel } from '@/GraphEditor/agent/AgentInGraphPanel';
import { AgentSpotlight } from '@/GraphEditor/agent/AgentSpotlight';
import { AgentConnectionLine } from '@/GraphEditor/agent/AgentConnectionLine';
import { AgentFunctionBadge } from '@/GraphEditor/agent/AgentFunctionBadge';
import { useAgentPanelDrag } from '@/GraphEditor/agent/useAgentPanelDrag';
import { MergeInGraphContextProvider } from '@/GraphEditor/drawingLayer/contexts/MergeInGraphContext';
import { NavigateToFunctionContextProvider } from '@/GraphEditor/drawingLayer/contexts/NavigateToFunctionContext';
import { GraphDispatchContextProvider } from '@/GraphEditor/drawingLayer/contexts/GraphDispatchContext';
import { FunctionsContextProvider } from '@/GraphEditor/drawingLayer/contexts/FunctionsContext';

const Container = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    max-width: 100vw;
    max-height: 100vh;
    overflow: scroll;

    ::-webkit-scrollbar {
        width: 11px;
        height: 11px;
    }
    ::-webkit-scrollbar-button {
        width: 0;
        height: 0;
    }
    ::-webkit-scrollbar-thumb {
        background: ${props => props.theme.backgroundColor};
        border: 1px solid ${props => props.theme.subtleAccentColor};
        border-radius: 50px;
    }
    ::-webkit-scrollbar-thumb:hover {
        background: ${props => props.theme.subtleAccentColor};
    }
    ::-webkit-scrollbar-thumb:active {
        background: ${props => props.theme.subtleAccentColor};
    }
    ::-webkit-scrollbar-track {
        background: ${props => props.theme.backgroundColor};
        border: 0 none #ffffff;
        border-radius: 53px;
    }
    ::-webkit-scrollbar-track:hover {
        background: ${props => props.theme.elevatedBackgroundColor};
    }
    ::-webkit-scrollbar-track:active {
        background: ${props => props.theme.elevatedBackgroundColor};
    }
    ::-webkit-scrollbar-corner {
        background: transparent;
    }
`;

interface ScaleContainerProps {
    width: number;
    height: number;
}

export const ScaleContainer = styled.div<ScaleContainerProps>`
    width: ${props => props.width}px;
    height: ${props => props.height}px;
    overflow: hidden;
    user-select: none;
`;

interface PositioningContainerProps {
    width: number;
    height: number;
    scale: number;
}

export const PositioningContainer = styled.div<PositioningContainerProps>`
    position: absolute;
    top: 0;
    left: 0;
    width: ${props => props.width}px;
    height: ${props => props.height}px;
    transform: scale(${props => props.scale});
    transform-origin: 0 0;
    user-select: none;
    pointer-events: none;
`;

const SVG = styled.svg`
    user-select: none;
    background-color: ${props => props.theme.backgroundColor};
`;

export type NodePortPositions = {
    [key: number]: {
        inPortPositions: { [key: string]: Vector };
        outPortPositions: { [key: string]: Vector };
    };
};

interface Props {
    // Graph data
    nodeInstances: NodeInstance[];
    connections: DataConnection[];

    // Agent data
    agents?: AgentInstance[];
    agentManager?: AgentManager;
    currentFunctionId?: string | null;

    // Function data
    currentFunctionDefinition?: any; // Will be FunctionDefinition when editing a function
    updateFunctionInputs?: (functionId: string, inputPorts: FunctionPortDefinition[]) => void;
    updateFunctionOutputs?: (functionId: string, outputPorts: FunctionPortDefinition[]) => void;

    // Graph operations
    addNode: (libraryElement: LibraryElement, position: Vector) => void;
    addConnection: (connection: Omit<DataConnection, 'id'>) => void;
    removeConnection: (index: number) => void;
    mergeInGraph: (nodeInstances: NodeInstance[], connections: DataConnection[], deselectOld?: boolean) => number[];
    moveNodes: (delta: Vector) => void;
    moveNodesEnd: () => void;
    removeNodes: (nodeIds: number[]) => void;
    layoutGraph: (portPositions?: NodePortPositions) => void;
    selectNodes: (nodeIds: number[]) => void;
    selectBranch: (nodeId: number) => void;
    selectBranchAdd: (nodeId: number) => void;
    selectOnlyOneNode: (nodeId: number) => void;
    deselectAllNodes: () => void;
    deselectNodes: (nodeIds: number[]) => void;
    reframeNode: (nodeId: number, position: Vector, width: number, height: number) => void;
    reframeNodeEnd: () => void;
    setNodeDimensions: (nodeInstanceId: number, width: number, height: number) => void;
    setNodeState: (nodeInstanceId: number, state: NodeInstanceState) => void;
    setNodeShape: (nodeInstanceId: number, shape: { inPorts: NodeInstanceDataPort[]; outPorts: NodeInstanceDataPort[] }) => void;

    // Navigation
    navigateToFunction: (functionId: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatchGraphAction?: (action: any) => void;
    viewportInfoRef?: React.MutableRefObject<{
        containerRef: React.RefObject<HTMLDivElement>;
        zoomLevel: number;
    } | null>;

    nodeLibrary: LibraryElement[];
    functions: FunctionDefinition[];
    nodeDefinitions: Record<string, Node>;
    callIntegration: (integrationAccess: IntegrationAccess, data: any) => Promise<any>;

    // Other props
    executionResult?: ExecutionResult;
    executionError?: ExecutionError;
    validationResult?: ValidationResult;
    triggerManualRun: (nodeInstanceId: number) => void;
    undo: () => void;
    redo: () => void;
    ranNodes: NodeRunRecordDictionary;
    integrationAccesses: IntegrationAccess[];
    renderOverlayContent: (
        nodeInstance: NodeInstance,
        portName: string,
        portSide: PortOverlaySide,
        closeOverlay: () => void,
    ) => React.ReactNode;

    // Run viewing props
    isViewingRun?: boolean;
    livePreviewEnabled?: boolean;
    onToggleLivePreview?: () => void;
    runDetail?: GraphRunDetail | null;
    isLoadingRunDetail?: boolean;
    onCloseRunView?: () => void;
    compatibility?: RunCompatibility | null;

    // Collaboration
    cursorPeers?: CollaboratorPresence[];
    onCursorMove?: (graphPosition: Vector) => void;
}

export const GraphEditorDrawing: React.FC<Props> = ({
    // Graph data
    nodeInstances,
    connections,
    // Agent data
    agents = [],
    agentManager,
    currentFunctionId,
    // Function data
    currentFunctionDefinition,
    updateFunctionInputs,
    updateFunctionOutputs,
    // Graph operations
    addNode,
    addConnection,
    removeConnection,
    mergeInGraph,
    moveNodes,
    moveNodesEnd,
    selectNodes,
    selectBranch,
    selectBranchAdd,
    selectOnlyOneNode,
    deselectAllNodes,
    deselectNodes,
    removeNodes,
    layoutGraph: applyLayout,
    reframeNode,
    reframeNodeEnd,
    setNodeDimensions,
    setNodeState,
    setNodeShape,

    // Navigation
    navigateToFunction,
    dispatchGraphAction,
    viewportInfoRef,
    callIntegration,

    // Other props
    nodeLibrary,
    functions,
    nodeDefinitions,
    executionResult,
    executionError,
    validationResult,
    triggerManualRun,
    undo,
    redo,
    ranNodes,
    integrationAccesses,
    renderOverlayContent,

    // Run viewing
    isViewingRun,
    livePreviewEnabled,
    onToggleLivePreview,
    runDetail,
    isLoadingRunDetail,
    onCloseRunView,
    compatibility,

    // Collaboration
    cursorPeers,
    onCursorMove,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const theme = useContext<ThemeType>(ThemeContext);

    // Force re-render when container resizes (e.g. sidebar toggle) so port
    // useLayoutEffects re-measure positions with the updated containerRect.
    const [layoutVersion, setLayoutVersion] = useState(0);
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            const observer = new ResizeObserver(() => {
                setLayoutVersion(n => n + 1);
            });
            observer.observe(container);
            return () => observer.disconnect();
        }
    }, []);

    const [activeOverlay, setActiveOverlay] = useState<PortOverlayState | null>(null);
    const openOverlay = useCallback((nodeInstanceId: number, portName: string, portSide: PortOverlaySide) => {
        setActiveOverlay({ nodeInstanceId, portName, portSide });
    }, []);
    const closeOverlay = useCallback(() => {
        setActiveOverlay(null);
    }, []);

    // Close output overlay when its execution result disappears (e.g. run view closed)
    useEffect(() => {
        if (activeOverlay?.portSide === 'out' && !executionResult?.[activeOverlay.nodeInstanceId]) {
            setActiveOverlay(null);
        }
    }, [activeOverlay, executionResult]);

    // Create nodeDictionary from context nodeInstances
    const nodeDictionary = useMemo(
        () => dictionaryFromNumberTuple(nodeInstances, nodeInstance => [nodeInstance.id, nodeInstance]),
        [nodeInstances],
    );

    const latestMousePosition = useRef<Vector>({ x: 0, y: 0 });

    const nodeConnectionDictionary = useMemo(() => {
        const nodePortsMap: Record<string, Record<string, boolean>> = {};

        connections.forEach(connection => {
            const sendingNodeInstanceId = connection.sendingNodeInstanceId.toString();
            const sendingPortName = connection.sendingPortName;
            const receivingNodeInstanceId = connection.receivingNodeInstanceId.toString();
            const receivingPortName = connection.receivingPortName;

            // Add sending port
            if (!nodePortsMap[sendingNodeInstanceId]) {
                nodePortsMap[sendingNodeInstanceId] = {};
            }
            nodePortsMap[sendingNodeInstanceId][sendingPortName] = true;

            // Add receiving port
            if (!nodePortsMap[receivingNodeInstanceId]) {
                nodePortsMap[receivingNodeInstanceId] = {};
            }
            nodePortsMap[receivingNodeInstanceId][receivingPortName] = true;
        });

        return nodePortsMap;
    }, [connections]);

    const connectionValidationDictionary = dictionaryFromStringTuple(validationResult?.connectionValidations ?? [], validation => [
        validation.id,
        validation,
    ]);

    useEffect(() => {
        const containerElement = containerRef.current;
        if (containerElement) {
            containerElement.scrollTo(DRAW_CONFIG.BASE_SIZE / 2, DRAW_CONFIG.BASE_SIZE / 2);
        }
    }, []);

    const { zoomLevel, frameBoundsInView } = useZooming(latestMousePosition, containerRef);

    if (viewportInfoRef) {
        viewportInfoRef.current = {
            containerRef,
            zoomLevel,
        };
    }

    const [nodePortPositions, setNodePortPositions] = useState<NodePortPositions>({});

    const zoomLevelRef = useRef<number>(zoomLevel);
    zoomLevelRef.current = zoomLevel;

    // After a zoom change, re-measure port positions once the layout has settled.
    // Memoized NodeRenderers don't receive zoomLevel, so bumping layoutVersion is
    // what forces them to re-render and re-run their port-measuring useLayoutEffects.
    // Keyed on zoomLevel only: container resizes are handled by the ResizeObserver
    // above, and newly added nodes mount and measure synchronously at the current
    // zoom — so re-measuring on every nodeInstances/connections change just causes a
    // 2-frame snap (visible flashing) without fixing anything.
    useEffect(() => {
        let firstFrame = 0;
        let secondFrame = 0;
        firstFrame = requestAnimationFrame(() => {
            secondFrame = requestAnimationFrame(() => {
                setLayoutVersion(version => version + 1);
            });
        });
        return () => {
            cancelAnimationFrame(firstFrame);
            cancelAnimationFrame(secondFrame);
        };
    }, [zoomLevel]);

    const setInPortPosition = useCallback((nodeInstanceId: number, portName: string, position: Vector) => {
        const containerElement = containerRef.current;
        if (containerElement) {
            const containerRect = containerElement.getBoundingClientRect();
            // Capture scroll + zoom synchronously with the measured rect. Reading them
            // lazily inside the setState updater lets React flush it in a later render
            // where zoom/scroll have already advanced (mid zoom gesture), so the rect
            // gets divided by a mismatched zoom and the endpoint flings to a corner.
            const offsetPosition = {
                x: (position.x - containerRect.x + containerElement.scrollLeft) / zoomLevelRef.current,
                y: (position.y - containerRect.y + containerElement.scrollTop) / zoomLevelRef.current,
            };
            setNodePortPositions(prevPositions => {
                const newPositions = {
                    ...prevPositions,
                };
                if (newPositions[nodeInstanceId]) {
                    newPositions[nodeInstanceId].inPortPositions[portName] = offsetPosition;
                } else {
                    newPositions[nodeInstanceId] = {
                        inPortPositions: {
                            [portName]: offsetPosition,
                        },
                        outPortPositions: {},
                    };
                }
                return newPositions;
            });
        }
    }, []);

    const setOutPortPosition = useCallback((nodeInstanceId: number, portName: string, position: Vector) => {
        const containerElement = containerRef.current;
        if (containerElement) {
            const containerRect = containerElement.getBoundingClientRect();
            // Capture scroll + zoom synchronously with the measured rect (see setInPortPosition).
            const offsetPosition = {
                x: (position.x - containerRect.x + containerElement.scrollLeft) / zoomLevelRef.current,
                y: (position.y - containerRect.y + containerElement.scrollTop) / zoomLevelRef.current,
            };
            setNodePortPositions(prevPositions => {
                const newPositions = {
                    ...prevPositions,
                };

                if (newPositions[nodeInstanceId]) {
                    newPositions[nodeInstanceId].outPortPositions[portName] = offsetPosition;
                } else {
                    newPositions[nodeInstanceId] = {
                        outPortPositions: {
                            [portName]: offsetPosition,
                        },
                        inPortPositions: {},
                    };
                }
                return newPositions;
            });
        }
    }, []);

    const { viewDrag, setViewDrag, handleViewDragStart, handleViewDragMove, handleViewDragEnd } = useViewDrag(containerRef, svgRef);

    const { nodeDrag, setNodeDrag, startNodeDrag, updateNodeDrag } = useNodeDrag(moveNodes, containerRef, zoomLevel);

    const {
        isConnectionDragActive,
        setConnectionDrag,
        handleConnectionDragMove,
        handleOutPortMouseDown,
        handleInPortMouseUp,
        handleInPortMouseDown,
        handleConnectionMouseDown,
        drawConnectionDrag,
    } = useConnectionDrag(
        { nodeInstances: nodeInstances, connections },
        nodePortPositions,
        addConnection,
        removeConnection,
        containerRef,
        zoomLevel,
        setViewDrag,
    );

    const { isResizeActive, onResizeStart, onResizeUpdate, onResizeEnd } = useNodeResizing(
        reframeNode,
        reframeNodeEnd,
        containerRef,
        zoomLevel,
    );

    const { reframe } = useGraphFraming({ nodeInstances: nodeInstances, connections }, frameBoundsInView);

    const {
        nodeLibrary: nodeLibraryState,
        showNodeLibrary,
        toggleNodeLibrary,
        toggleNodeLibraryAtCenter,
        hideNodeLibrary,
        renderNodeLibrary,
        renderPreviewNode,
    } = useNodeLibrary(
        addNode,
        nodeLibrary,
        nodeDefinitions,
        containerRef,
        zoomLevel,
        integrationAccesses,
        callIntegration,
        functions,
        latestMousePosition,
    );

    useCopyAndPaste(containerRef, zoomLevel, { nodeInstances: nodeInstances, connections }, mergeInGraph);

    // Agent panel dragging
    const agentPanelDrag = useAgentPanelDrag(agentManager?.updateAgentPosition ?? (() => {}), zoomLevel);

    // Filter agents by current view context
    const agentsInView = agents.filter(agent => agent.functionId === (currentFunctionId ?? null));
    const agentsElsewhere = agents.filter(agent => agent.functionId !== (currentFunctionId ?? null));

    const { handleSelectionStart, handleSelectionMove, handleRectangleSelectionEnd, rectangleSelectionActive, drawRectangleSelection } =
        useRectangleSelection({ nodeInstances: nodeInstances, connections }, containerRef, zoomLevel, selectNodes);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement> | KeyboardEvent<SVGElement>) => {
        if (event.ctrlKey || event.metaKey) {
            switch (event.key) {
                case 'z': {
                    event.preventDefault();
                    undo();
                    break;
                }
                case 'y': {
                    event.preventDefault();
                    redo();
                    break;
                }
                case 'k': {
                    event.preventDefault();
                    if (isConnectionDragActive || nodeDrag || viewDrag) {
                        return;
                    }
                    showNodeLibrary();
                    break;
                }
            }
        }
        switch (event.key) {
            case 'Tab': {
                event.preventDefault();
                if (isConnectionDragActive || nodeDrag || viewDrag) {
                    return;
                }
                showNodeLibrary();
                break;
            }
            case 'Backspace':
            case 'Delete': {
                event.preventDefault();
                const selectedNodes = nodeInstances.filter(node => node.selected);
                const protectedNodeIds = currentFunctionDefinition
                    ? [currentFunctionDefinition.functionInputsNodeId, currentFunctionDefinition.functionOutputsNodeId]
                    : [];
                const deletableIds = selectedNodes.filter(node => !protectedNodeIds.includes(node.id)).map(node => node.id);
                if (deletableIds.length < selectedNodes.length) {
                    toast.error('Function Inputs and Outputs nodes cannot be deleted');
                }
                if (deletableIds.length > 0) {
                    removeNodes(deletableIds);
                }
                break;
            }
            case 'f': {
                reframe();
                break;
            }
            case 'a': {
                selectNodes(nodeInstances.map(node => node.id));
                break;
            }
            case 'r': {
                event.preventDefault();
                applyLayout(nodePortPositions);
                break;
            }
            case 'Escape': {
                event.preventDefault();
                if (activeOverlay) {
                    closeOverlay();
                } else if (nodeLibraryState.browsing) {
                    hideNodeLibrary();
                } else {
                    deselectAllNodes();
                }
                break;
            }
        }
    };

    const [hoveredConnectionId, setHoveredConnectionId] = useState<number | undefined>(undefined);
    const handleConnectionHover = useCallback((connectionId: number, hovered: boolean) => {
        if (hovered) {
            setHoveredConnectionId(connectionId);
        } else {
            setHoveredConnectionId(undefined);
        }
    }, []);

    // This code has a lot of duplication with the code inside the GraphConnection
    // Once the Connections are lifted out into HTML this can be re-integrated into the GraphConnection
    const drawConnectionHoverMessage = () => {
        if (hoveredConnectionId) {
            const connection = connections.find(connection => connection.id === hoveredConnectionId);
            if (connection) {
                const sendingNodeInstance = nodeDictionary[connection.sendingNodeInstanceId];
                if (sendingNodeInstance) {
                    const startPosition = nodePortPositions[connection.sendingNodeInstanceId]?.outPortPositions[connection.sendingPortName];
                    const endPosition =
                        nodePortPositions[connection.receivingNodeInstanceId]?.inPortPositions[connection.receivingPortName];
                    // Positions register via useLayoutEffect in the port components; a freshly
                    // merged-in node has a frame where its connection exists but its ports
                    // haven't measured yet. Skip rendering until both endpoints are known.
                    if (!startPosition || !endPosition) return null;
                    const validation = connectionValidationDictionary[hoveredConnectionId];
                    const messagePosition = interpolateVectors(startPosition, endPosition, 0.5);
                    let sendingPort: NodeInstanceDataPort | undefined;

                    switch (connection.sendingPortType) {
                        case 'external': {
                            sendingPort = sendingNodeInstance.outPorts.find(port => port.name === connection.sendingPortName);
                            if (!sendingPort) return null;
                            break;
                        }
                        case 'inherent': {
                            sendingPort = inherentOutPortTemplate;
                            break;
                        }
                    }
                    if (validation && !validation.valid && validation.errorMessage) {
                        return (
                            <GraphMessage position={messagePosition} color={'white'} backgroundColor={theme.errorColor}>
                                {validation.errorMessage}
                            </GraphMessage>
                        );
                    } else if (sendingPort?.type) {
                        const color = !validation || validation.valid ? getColorFromDataType(sendingPort?.type, theme) : 'red';
                        return (
                            <GraphMessage position={messagePosition} color={'white'} backgroundColor={color}>
                                {getStringRepresentationFromType(sendingPort.type)}
                            </GraphMessage>
                        );
                    }
                }
            }
        }
        return null;
    };

    const drawConnection = (connection: DataConnection) => {
        const startPosition = nodePortPositions[connection.sendingNodeInstanceId]?.outPortPositions[connection.sendingPortName];
        const endPosition = nodePortPositions[connection.receivingNodeInstanceId]?.inPortPositions[connection.receivingPortName];

        if (startPosition && endPosition) {
            return (
                <GraphConnection
                    key={connection.receivingPortName + connection.receivingNodeInstanceId + connection.sendingNodeInstanceId}
                    startPosition={startPosition}
                    endPosition={endPosition}
                    connection={connection}
                    theme={theme}
                    sendingNodeInstance={nodeDictionary[connection.sendingNodeInstanceId]}
                    receivingNodeInstance={nodeDictionary[connection.receivingNodeInstanceId]}
                    onMouseDown={handleConnectionMouseDown}
                    validation={connectionValidationDictionary[connection.id]}
                    onHoverChange={handleConnectionHover}
                />
            );
        }
        return null;
    };

    const [dragCheck, setDragCheck] = useState<number | null>(null);
    const handleGraphMouseDown = (event: PointerEvent<SVGElement> | PointerEvent<HTMLDivElement>) => {
        // Close port overlay on canvas background click (but not output value overlays)
        if (activeOverlay && activeOverlay.portSide !== 'out') {
            closeOverlay();
        }

        switch (event.button) {
            case 2: {
                if (nodeLibraryState.browsing) {
                    hideNodeLibrary();
                } else {
                    showNodeLibrary();
                }
                break;
            }
            default: {
                if (event.shiftKey) {
                    handleSelectionStart(event);
                } else {
                    handleViewDragStart(event);
                    setDragCheck(0);
                }
            }
        }
    };

    // Relay methods
    const handleGraphMouseMove = (event: MouseEvent<SVGElement> | MouseEvent<HTMLDivElement>) => {
        const containerElement = containerRef.current;
        if (containerElement) {
            latestMousePosition.current = getInElementPositionFromEvent(event, containerElement);
            if (onCursorMove) {
                const graphPos = screenToWorldCoordinate(
                    latestMousePosition.current,
                    { x: containerElement.scrollLeft, y: containerElement.scrollTop },
                    zoomLevelRef.current,
                );
                onCursorMove(graphPos);
            }
        }
        if (agentPanelDrag.isDragging) {
            agentPanelDrag.updateDrag(event as unknown as React.PointerEvent<HTMLDivElement>);
        } else if (nodeDrag) {
            updateNodeDrag(event);
        } else if (isConnectionDragActive) {
            handleConnectionDragMove(event);
        } else if (viewDrag) {
            handleViewDragMove(event);
        } else if (isResizeActive) {
            onResizeUpdate(event);
        }

        if (rectangleSelectionActive) {
            handleSelectionMove(event);
        }

        if (dragCheck !== null) {
            setDragCheck(dragCheck + 1);
        }
    };

    const handleGraphMouseUp = (event: PointerEvent) => {
        event.preventDefault();
        if (agentPanelDrag.isDragging) {
            agentPanelDrag.endDrag();
        }
        if (nodeDrag) {
            moveNodesEnd();
        }

        if (rectangleSelectionActive) {
            handleRectangleSelectionEnd();
        }

        setNodeDrag(null);
        setConnectionDrag(null);
        handleViewDragEnd(event);
        if (isResizeActive) {
            onResizeEnd();
        }
    };

    const handleGraphClick = () => {
        if (dragCheck !== null && dragCheck < 4) {
            deselectAllNodes();
        }
        setDragCheck(null);

        if (nodeLibraryState.browsing) {
            hideNodeLibrary();
        }
    };

    const handleNodeClick = useCallback((event: MouseEvent) => {
        event.stopPropagation();
        return false;
    }, []);

    // Handle function node navigation
    const handleFunctionNodeDoubleClick = useCallback(
        (event: MouseEvent, nodeInstance: NodeInstance) => {
            if (Object.hasOwn(nodeInstance, 'functionId')) {
                // FIXME The FunctionID type needs to be clarified
                navigateToFunction((nodeInstance as FunctionNodeInstance).functionId);
            }
        },
        [navigateToFunction, nodeDefinitions],
    );

    const handleNodeMouseDown = useCallback(
        (event: PointerEvent, eventNode: NodeInstance) => {
            if (activeOverlay?.portSide !== 'out') {
                closeOverlay();
            }
            event.stopPropagation();
            const svgElement = svgRef.current;
            if (svgElement) {
                svgElement.setPointerCapture(event.pointerId);
            }
            switch (event.button) {
                // Main (left) mouse button
                case 0: {
                    if (event.shiftKey) {
                        if (!eventNode.selected) {
                            selectNodes([eventNode.id]);
                        }
                    } else if (!eventNode.selected) {
                        selectOnlyOneNode(eventNode.id);
                    }
                    break;
                }

                // Auxiliary (middle) mouse button
                case 1: {
                    if (event.shiftKey) {
                        selectBranchAdd(eventNode.id);
                    } else {
                        selectBranch(eventNode.id);
                    }
                    break;
                }
            }

            startNodeDrag(event);
        },
        [activeOverlay, closeOverlay, startNodeDrag, selectNodes, selectBranch, selectBranchAdd, selectOnlyOneNode],
    );

    const handleNodeMouseUp = useCallback(() => {}, []);

    const drawNode = (nodeInstance: NodeInstance) => {
        const nodeDefinition = nodeDefinitions[nodeInstance.nodeId];
        if (!nodeDefinition) {
            console.error(`Node definition not found for nodeId: ${nodeInstance.nodeId}`);
            return null;
        }

        return (
            <NodeRenderer
                nodeInstance={nodeInstance}
                node={nodeDefinition}
                key={nodeInstance.id}
                currentFunctionDefinition={currentFunctionDefinition}
                updateFunctionInputs={updateFunctionInputs}
                updateFunctionOutputs={updateFunctionOutputs}
                runTimes={ranNodes[nodeInstance.id]}
                handleMouseDown={handleNodeMouseDown}
                handleMouseUp={handleNodeMouseUp}
                onClick={handleNodeClick}
                onDoubleClick={handleFunctionNodeDoubleClick}
                onOutPortMouseDown={handleOutPortMouseDown}
                onInPortMouseUp={handleInPortMouseUp}
                onInPortMouseDown={handleInPortMouseDown}
                setInPortPosition={setInPortPosition}
                setOutPortPosition={setOutPortPosition}
                layoutVersion={layoutVersion}
                triggerManualRun={triggerManualRun}
                callIntegration={callIntegration}
                setNodeDimensions={setNodeDimensions}
                setNodeState={setNodeState}
                setNodeShape={setNodeShape}
                executionError={executionError}
                executionResult={executionResult?.[nodeInstance.id]}
                nodeConnectionDictionary={nodeConnectionDictionary}
                startNodeReframe={onResizeStart}
                endNodeReframe={onResizeEnd}
                activeOverlay={activeOverlay}
                openPortOverlay={openOverlay}
                closePortOverlay={closeOverlay}
            />
        );
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <Container
                ref={containerRef}
                tabIndex={0}
                onPointerDown={handleGraphMouseDown}
                onPointerMove={handleGraphMouseMove}
                onPointerUp={handleGraphMouseUp}
                onKeyDown={handleKeyDown}
            >
                <ScaleContainer width={DRAW_CONFIG.BASE_SIZE * zoomLevel} height={DRAW_CONFIG.BASE_SIZE * zoomLevel}>
                    <SVG ref={svgRef} viewBox={`0 0 ${DRAW_CONFIG.BASE_SIZE} ${DRAW_CONFIG.BASE_SIZE}`} onClick={handleGraphClick}>
                        <GraphBackgroundPattern />
                        {drawConnectionDrag()}
                        {connections.map(drawConnection)}
                        {agentsInView.map(
                            agent =>
                                agent.activeNodeId != null && (
                                    <React.Fragment key={`agent-svg-${agent.agentId}`}>
                                        <AgentSpotlight agent={agent} nodeInstances={nodeInstances} />
                                        <AgentConnectionLine agent={agent} nodeInstances={nodeInstances} />
                                    </React.Fragment>
                                ),
                        )}
                        {drawRectangleSelection()}
                    </SVG>
                    <FunctionsContextProvider value={functions}>
                        <NavigateToFunctionContextProvider value={navigateToFunction}>
                            <GraphDispatchContextProvider value={dispatchGraphAction ?? null}>
                                <MergeInGraphContextProvider value={mergeInGraph}>
                                    <PositioningContainer width={DRAW_CONFIG.BASE_SIZE} height={DRAW_CONFIG.BASE_SIZE} scale={zoomLevel}>
                                        {nodeInstances.map(drawNode)}
                                        {renderPreviewNode()}
                                        {drawConnectionHoverMessage()}
                                        {agentManager &&
                                            agentsInView.map(agent => (
                                                <AgentInGraphPanel
                                                    key={agent.agentId}
                                                    agent={agent}
                                                    session={agentManager.getAgentSession(agent.agentId)}
                                                    onDragStart={agentPanelDrag.startDrag}
                                                    onToggleExpanded={agentManager.toggleAgentExpanded}
                                                    onRemove={agentManager.removeAgent}
                                                />
                                            ))}
                                        {agentManager &&
                                            agentsElsewhere.map(agent => (
                                                <AgentFunctionBadge
                                                    key={`badge-${agent.agentId}`}
                                                    agent={agent}
                                                    functions={functions}
                                                    navigateToFunction={navigateToFunction}
                                                />
                                            ))}
                                        <CollaboratorCursors peers={cursorPeers ?? []} zoomLevel={zoomLevel} />
                                    </PositioningContainer>
                                </MergeInGraphContextProvider>
                            </GraphDispatchContextProvider>
                        </NavigateToFunctionContextProvider>
                    </FunctionsContextProvider>
                </ScaleContainer>
            </Container>
            {renderNodeLibrary()}
            <NavigationLayer>
                {nodeInstances.length === 0 && !nodeLibraryState.browsing ? (
                    <CentralGraphOverlayMessage>
                        No Blocks in the Workflow yet. Right click or press TAB or{' '}
                        {window.navigator.userAgent.includes('Mac') ? '⌘' : 'Ctrl+'}K to open the Block Library
                    </CentralGraphOverlayMessage>
                ) : null}
                <ShowNodeLibraryButton onNodeLibraryButtonClick={() => toggleNodeLibraryAtCenter()} />
            </NavigationLayer>
            {isViewingRun && onCloseRunView && (
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    <RunInfoBar
                        runDetail={runDetail ?? null}
                        isLoading={isLoadingRunDetail ?? false}
                        onClose={onCloseRunView}
                        compatibility={compatibility ?? null}
                        livePreviewEnabled={livePreviewEnabled ?? true}
                        onToggleLivePreview={onToggleLivePreview}
                    />
                </div>
            )}
            <PortOverlayLayer
                containerRef={containerRef}
                zoomLevel={zoomLevel}
                nodePortPositions={nodePortPositions}
                nodeInstances={nodeInstances}
                activeOverlay={activeOverlay}
                openOverlay={openOverlay}
                closeOverlay={closeOverlay}
                renderOverlayContent={renderOverlayContent}
            />
        </div>
    );
};
